'use client';

import { type CSSProperties, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ListFilter, Maximize2 } from 'lucide-react';
import { MasonryPhotoAlbum, type Photo, RowsPhotoAlbum } from 'react-photo-album';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import type { Image } from '../../../types';

export type ImagesGalleryTermId = string | number;

export type ImagesGalleryTerm = {
    id: ImagesGalleryTermId;
    label: string;
};

export type ImagesGalleryImage = Image & {
    termIds?: ImagesGalleryTermId[];
    galleryCaption?: string;
    lightboxCaption?: string;
};

export type ImagesGalleryProps = {
    images: ImagesGalleryImage[];
    showContactSnippet?: boolean;
    terms?: ImagesGalleryTerm[];
    defaultSelectedTermIds?: ImagesGalleryTermId[];
    layout?: 'rows' | 'masonry';
    columns?: number;
    spacing?: number;
    targetRowHeight?: number;
    initialVisibleCount?: number;
    loadMoreCount?: number;
    showMoreLabel?: string;
    showGalleryCaptions?: boolean;
    showLightboxCaptions?: boolean;
    showMobileFilterButton?: boolean;
    mobileFilterButtonLabel?: string;
    filterTitle?: string;
    selectAllLabel?: string;
    deselectAllLabel?: string;
    emptyMessage?: string;
    accentColor?: string;
    ariaLabel?: string;
    showDebugIndex?: boolean;
};

type ImagesGalleryStyle = CSSProperties & {
    '--images-gallery-accent'?: string;
};

type GalleryPhoto = Photo & {
    source: ImagesGalleryImage;
};

const imagesGalleryCss = `
.images-gallery {
  --images-gallery-accent: #b80000;
  --images-gallery-text: #172033;
  --images-gallery-text-soft: #667085;
  --images-gallery-border: #e4e7ec;
  --images-gallery-surface: #ffffff;
  --images-gallery-surface-soft: #f8fafc;
  width: 100%;
  min-width: 0;
  color: var(--images-gallery-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  container-type: inline-size;
}

.images-gallery,
.images-gallery * {
  box-sizing: border-box;
}

.images-gallery-layout {
  display: grid;
  grid-template-columns: minmax(190px, 240px) minmax(0, 1fr);
  gap: clamp(20px, 3vw, 34px);
  align-items: start;
}

.images-gallery-sidebar {
  position: sticky;
  top: 16px;
  scroll-margin-top: 16px;
  min-width: 0;
  padding: 18px;
  border: 1px solid var(--images-gallery-border);
  border-radius: 12px;
  background: var(--images-gallery-surface);
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.05);
}

.images-gallery-sidebar:focus {
  outline: 3px solid color-mix(in srgb, var(--images-gallery-accent) 30%, transparent);
  outline-offset: 3px;
}

.images-gallery-fieldset {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.images-gallery-legend {
  width: 100%;
  margin: 0 0 14px;
  padding: 0;
  color: var(--images-gallery-text);
  font-size: 15px;
  line-height: 1.25;
  font-weight: 850;
}

.images-gallery-filter-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 14px;
}

.images-gallery-filter-action {
  min-height: 36px;
  border: 1px solid var(--images-gallery-border);
  border-radius: 8px;
  background: var(--images-gallery-surface-soft);
  color: var(--images-gallery-text);
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 800;
  padding: 7px 9px;
  transition: border-color 160ms ease, color 160ms ease, background-color 160ms ease;
}

.images-gallery-filter-action:hover:not(:disabled) {
  border-color: var(--images-gallery-accent);
  color: var(--images-gallery-accent);
  background: var(--images-gallery-surface);
}

.images-gallery-filter-action:focus-visible,
.images-gallery-filter-checkbox:focus-visible,
.images-gallery .react-photo-album--button:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--images-gallery-accent) 30%, transparent);
  outline-offset: 3px;
}

.images-gallery-filter-action:disabled {
  cursor: default;
  opacity: 0.46;
}

.images-gallery-filter-list {
  display: grid;
  gap: 4px;
}

.images-gallery-filter-option {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-height: 40px;
  padding: 7px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 160ms ease;
}

.images-gallery-filter-option:hover {
  background: var(--images-gallery-surface-soft);
}

.images-gallery-filter-checkbox {
  width: 17px;
  height: 17px;
  margin: 0;
  accent-color: var(--images-gallery-accent);
  cursor: pointer;
}

.images-gallery-filter-label {
  min-width: 0;
  color: var(--images-gallery-text);
  font-size: 13px;
  line-height: 1.35;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.images-gallery-filter-count {
  display: inline-flex;
  min-width: 25px;
  height: 25px;
  align-items: center;
  justify-content: center;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--images-gallery-surface-soft);
  color: var(--images-gallery-text-soft);
  font-size: 11px;
  line-height: 1;
  font-weight: 850;
}

.images-gallery-content {
  min-width: 0;
}

.images-gallery-results {
  margin: 0 0 12px;
  color: var(--images-gallery-text-soft);
  font-size: 13px;
  line-height: 1.4;
  font-weight: 700;
}

.images-gallery-show-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 22px;
}

.images-gallery-show-more {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--images-gallery-accent);
  border-radius: 8px;
  background: var(--images-gallery-accent);
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  line-height: 1;
  font-weight: 850;
  padding: 0 18px;
  transition: filter 160ms ease, transform 160ms ease;
}

.images-gallery-show-more:hover {
  filter: brightness(0.94);
  transform: translateY(-1px);
}

.images-gallery-show-more:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--images-gallery-accent) 30%, transparent);
  outline-offset: 3px;
}

.images-gallery .react-photo-album--photo {
  overflow: hidden;
  border-radius: 10px;
  background: #eef1f5;
}

.images-gallery .react-photo-album--button {
  display: block;
  width: 100%;
  overflow: hidden;
  border-radius: inherit;
  color: inherit;
}

.images-gallery .react-photo-album--image {
  transform: scale(1);
  transition: filter 220ms ease, transform 320ms ease;
}

.images-gallery-tile-caption {
  position: absolute;
  z-index: 2;
  inset: auto 0 0;
  margin: 0;
  padding: 38px 12px 12px;
  background: linear-gradient(to top, rgba(15, 23, 42, 0.84), rgba(15, 23, 42, 0));
  color: #ffffff;
  font-size: 13px;
  line-height: 1.35;
  font-weight: 750;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  overflow-wrap: anywhere;
}

.images-gallery .react-photo-album--button:hover .react-photo-album--image,
.images-gallery .react-photo-album--button:focus-visible .react-photo-album--image {
  filter: saturate(1.04);
  transform: scale(1.025);
}

.images-gallery-open-icon {
  display: inline-flex;
  position: absolute;
  z-index: 2;
  top: 10px;
  right: 10px;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.72);
  color: #ffffff;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.18);
  pointer-events: none;
  transition: background-color 160ms ease, transform 160ms ease;
}

.images-gallery .react-photo-album--button:hover .images-gallery-open-icon,
.images-gallery .react-photo-album--button:focus-visible .images-gallery-open-icon {
  background: var(--images-gallery-accent);
  transform: translateY(-1px);
}

.images-gallery-debug-index {
  position: absolute;
  z-index: 2;
  top: 10px;
  left: 10px;
  padding: 5px 8px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.74);
  color: #ffffff;
  font-size: 11px;
  line-height: 1;
  font-weight: 850;
  pointer-events: none;
}

.images-gallery-empty {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--images-gallery-border);
  border-radius: 10px;
  background: var(--images-gallery-surface-soft);
  color: var(--images-gallery-text-soft);
  font-size: 14px;
  line-height: 1.45;
  font-weight: 700;
  padding: 24px;
  text-align: center;
}

.images-gallery-lightbox {
  --yarl__slide_captions_container_background: rgba(0, 0, 0, 0.72);
  --yarl__slide_captions_container_padding: 14px 18px;
  --yarl__slide_title_color: rgba(255, 255, 255, 0.96);
}

.images-gallery-lightbox .yarl__slide_title {
  font-family: inherit;
  font-size: 15px;
  line-height: 1.45;
  font-weight: 700;
  text-align: center;
}

.images-gallery-mobile-filter-button {
  display: none;
  position: fixed;
  z-index: 30;
  right: max(18px, env(safe-area-inset-right));
  bottom: max(18px, env(safe-area-inset-bottom));
  width: 50px;
  height: 50px;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--images-gallery-accent) 76%, #000000);
  border-radius: 999px;
  background: var(--images-gallery-accent);
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.28);
  transition: filter 160ms ease, transform 160ms ease;
}

.images-gallery-mobile-filter-button:hover {
  filter: brightness(0.94);
  transform: translateY(-1px);
}

.images-gallery-mobile-filter-button:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--images-gallery-accent) 30%, transparent);
  outline-offset: 4px;
}

@supports not (outline: 3px solid color-mix(in srgb, red 30%, transparent)) {
  .images-gallery-filter-action:focus-visible,
  .images-gallery-filter-checkbox:focus-visible,
  .images-gallery .react-photo-album--button:focus-visible {
    outline-color: var(--images-gallery-accent);
  }
}

@container (max-width: 720px) {
  .images-gallery-layout {
    grid-template-columns: 1fr;
  }

  .images-gallery-sidebar {
    position: static;
  }

  .images-gallery-mobile-filter-button {
    display: inline-flex;
  }

  .images-gallery-filter-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@container (max-width: 430px) {
  .images-gallery-filter-list {
    grid-template-columns: 1fr;
  }
}
`;

const getTermKey = (id: ImagesGalleryTermId) => String(id);

const getImageTermKeys = (image: ImagesGalleryImage) =>
    new Set((image.termIds || []).map(getTermKey));

const getInitialSelection = (
    terms: ImagesGalleryTerm[],
    defaultSelectedTermIds?: ImagesGalleryTermId[],
) => {
    const availableTermKeys = new Set(terms.map(({ id }) => getTermKey(id)));
    const requestedTermKeys = defaultSelectedTermIds?.map(getTermKey);

    return new Set(
        requestedTermKeys
            ? requestedTermKeys.filter((termKey) => availableTermKeys.has(termKey))
            : availableTermKeys,
    );
};

const getPhoto = (image: ImagesGalleryImage): GalleryPhoto => {
    const width = image.width || 1728;
    const height = image.height || 1152;
    const alt = image.alt || image.title || 'Gallery image';

    return {
        src: image.url,
        width,
        height,
        alt,
        title: image.title,
        label: `Open ${alt} in full screen gallery`,
        source: image,
    };
};

const getGalleryCaption = (image: ImagesGalleryImage) =>
    image.galleryCaption || image.caption || '';

const getLightboxCaption = (image: ImagesGalleryImage) =>
    image.lightboxCaption || image.caption || image.title || '';

const getPositiveInteger = (value?: number) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0
        ? Math.max(1, Math.floor(value))
        : undefined;

const ImagesGallery = ({
    images,
    terms = [],
    defaultSelectedTermIds,
    layout,
    columns = 3,
    spacing = 10,
    targetRowHeight = 150,
    initialVisibleCount,
    loadMoreCount,
    showMoreLabel = 'Show more',
    showGalleryCaptions = true,
    showLightboxCaptions = true,
    showMobileFilterButton = true,
    mobileFilterButtonLabel = 'Back to gallery filters',
    filterTitle = 'Filter gallery',
    selectAllLabel = 'Select all',
    deselectAllLabel = 'Deselect all',
    emptyMessage = 'No images match the selected filters.',
    accentColor,
    ariaLabel = 'Image gallery',
    showDebugIndex = false,
}: ImagesGalleryProps) => {
    const [index, setIndex] = useState(-1);
    const filterPanelId = useId();
    const filterPanelRef = useRef<HTMLElement | null>(null);
    const initialLimit = getPositiveInteger(initialVisibleCount) ?? Number.POSITIVE_INFINITY;
    const loadMoreStep =
        getPositiveInteger(loadMoreCount) ?? getPositiveInteger(initialVisibleCount) ?? 12;
    const [visibleLimit, setVisibleLimit] = useState(initialLimit);
    const [selectedTermKeys, setSelectedTermKeys] = useState<Set<string>>(() =>
        getInitialSelection(terms, defaultSelectedTermIds),
    );
    const normalisedImages = useMemo(() => (images || []).filter((image) => !!image.url), [images]);
    const termKeys = useMemo(() => terms.map(({ id }) => getTermKey(id)), [terms]);
    const termCounts = useMemo(() => {
        const counts = new Map<string, number>();

        termKeys.forEach((termKey) => counts.set(termKey, 0));
        normalisedImages.forEach((image) => {
            getImageTermKeys(image).forEach((termKey) => {
                if (counts.has(termKey)) {
                    counts.set(termKey, (counts.get(termKey) || 0) + 1);
                }
            });
        });

        return counts;
    }, [normalisedImages, termKeys]);
    const allTermsSelected =
        termKeys.length > 0 && termKeys.every((termKey) => selectedTermKeys.has(termKey));
    const filteredImages = useMemo(() => {
        if (!terms.length || allTermsSelected) return normalisedImages;
        if (!selectedTermKeys.size) return [];

        return normalisedImages.filter((image) =>
            [...getImageTermKeys(image)].some((termKey) => selectedTermKeys.has(termKey)),
        );
    }, [allTermsSelected, normalisedImages, selectedTermKeys, terms.length]);
    const visibleImages = useMemo(
        () => filteredImages.slice(0, visibleLimit),
        [filteredImages, visibleLimit],
    );
    const photos = useMemo(() => visibleImages.map(getPhoto), [visibleImages]);
    const resolvedLayout = layout || (terms.length ? 'masonry' : 'rows');
    const hasProgressiveLimit = Number.isFinite(initialLimit);
    const hasMoreImages = photos.length < filteredImages.length;
    const remainingImageCount = filteredImages.length - photos.length;
    const resultLabel = hasProgressiveLimit
        ? `${photos.length} of ${filteredImages.length} images`
        : `${photos.length} ${photos.length === 1 ? 'image' : 'images'}`;
    const rootStyle: ImagesGalleryStyle = {
        '--images-gallery-accent': accentColor,
    };
    const albumProps = {
        photos,
        spacing,
        onClick: ({ index: photoIndex }: { index: number }) => setIndex(photoIndex),
        render: {
            extras: (
                _props: object,
                { index: photoIndex, photo }: { index: number; photo: GalleryPhoto },
            ) => {
                const galleryCaption = getGalleryCaption(photo.source);

                return (
                    <>
                        {showDebugIndex ? (
                            <span className="images-gallery-debug-index">{photoIndex}</span>
                        ) : null}
                        <span className="images-gallery-open-icon" aria-hidden="true">
                            <Maximize2 size={16} strokeWidth={2.4} />
                        </span>
                        {showGalleryCaptions && galleryCaption ? (
                            <span className="images-gallery-tile-caption">{galleryCaption}</span>
                        ) : null}
                    </>
                );
            },
        },
        componentsProps: {
            button: ({ photo }: { photo: GalleryPhoto }) => ({
                'aria-label': photo.label,
            }),
            image: {
                loading: 'lazy' as const,
                decoding: 'async' as const,
            },
        },
    };

    const selectAll = () => {
        setSelectedTermKeys(new Set(termKeys));
        setVisibleLimit(initialLimit);
        setIndex(-1);
    };

    const deselectAll = () => {
        setSelectedTermKeys(new Set());
        setVisibleLimit(initialLimit);
        setIndex(-1);
    };

    const toggleTerm = (termId: ImagesGalleryTermId) => {
        const termKey = getTermKey(termId);

        setSelectedTermKeys((currentTermKeys) => {
            const nextTermKeys = new Set(currentTermKeys);

            if (nextTermKeys.has(termKey)) {
                nextTermKeys.delete(termKey);
            } else {
                nextTermKeys.add(termKey);
            }

            return nextTermKeys;
        });
        setVisibleLimit(initialLimit);
        setIndex(-1);
    };

    const scrollToFilters = () => {
        const filterPanel = filterPanelRef.current;
        if (!filterPanel) return;

        filterPanel.focus({ preventScroll: true });
        filterPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    useEffect(() => {
        setVisibleLimit(initialLimit);
        setIndex(-1);
    }, [initialLimit]);

    if (!normalisedImages.length) return null;

    return (
        <section className="images-gallery" style={rootStyle} aria-label={ariaLabel}>
            <style>{imagesGalleryCss}</style>

            <div className={terms.length ? 'images-gallery-layout' : undefined}>
                {terms.length ? (
                    <aside
                        ref={filterPanelRef}
                        id={filterPanelId}
                        className="images-gallery-sidebar"
                        tabIndex={-1}
                    >
                        <fieldset className="images-gallery-fieldset">
                            <legend className="images-gallery-legend">{filterTitle}</legend>

                            <div className="images-gallery-filter-actions">
                                <button
                                    type="button"
                                    className="images-gallery-filter-action"
                                    disabled={allTermsSelected}
                                    onClick={selectAll}
                                >
                                    {selectAllLabel}
                                </button>
                                <button
                                    type="button"
                                    className="images-gallery-filter-action"
                                    disabled={!selectedTermKeys.size}
                                    onClick={deselectAll}
                                >
                                    {deselectAllLabel}
                                </button>
                            </div>

                            <div className="images-gallery-filter-list">
                                {terms.map((term) => {
                                    const termKey = getTermKey(term.id);

                                    return (
                                        <label
                                            className="images-gallery-filter-option"
                                            key={termKey}
                                        >
                                            <input
                                                type="checkbox"
                                                className="images-gallery-filter-checkbox"
                                                checked={selectedTermKeys.has(termKey)}
                                                onChange={() => toggleTerm(term.id)}
                                            />
                                            <span className="images-gallery-filter-label">
                                                {term.label}
                                            </span>
                                            <span className="images-gallery-filter-count">
                                                {termCounts.get(termKey) || 0}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </fieldset>
                    </aside>
                ) : null}

                <div className="images-gallery-content">
                    {terms.length ? (
                        <p className="images-gallery-results" aria-live="polite">
                            Showing {resultLabel}
                        </p>
                    ) : null}

                    {photos.length ? (
                        <>
                            {resolvedLayout === 'masonry' ? (
                                <MasonryPhotoAlbum
                                    {...albumProps}
                                    columns={(containerWidth) => {
                                        const preferredColumns =
                                            containerWidth < 480
                                                ? 1
                                                : containerWidth < 820
                                                  ? 2
                                                  : columns;

                                        return Math.max(1, Math.min(columns, preferredColumns));
                                    }}
                                />
                            ) : (
                                <RowsPhotoAlbum {...albumProps} targetRowHeight={targetRowHeight} />
                            )}

                            {hasMoreImages ? (
                                <div className="images-gallery-show-more-wrap">
                                    <button
                                        type="button"
                                        className="images-gallery-show-more"
                                        aria-label={`${showMoreLabel}. ${remainingImageCount} images remaining.`}
                                        onClick={() =>
                                            setVisibleLimit((currentLimit) =>
                                                Number.isFinite(currentLimit)
                                                    ? currentLimit + loadMoreStep
                                                    : currentLimit,
                                            )
                                        }
                                    >
                                        {showMoreLabel} ({remainingImageCount})
                                    </button>
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div className="images-gallery-empty">{emptyMessage}</div>
                    )}
                </div>
            </div>

            {terms.length && showMobileFilterButton ? (
                <button
                    type="button"
                    className="images-gallery-mobile-filter-button"
                    aria-label={mobileFilterButtonLabel}
                    aria-controls={filterPanelId}
                    onClick={scrollToFilters}
                >
                    <ListFilter size={22} strokeWidth={2.4} aria-hidden="true" />
                </button>
            ) : null}

            <Lightbox
                slides={photos.map(({ source, ...photo }) => ({
                    ...photo,
                    title: showLightboxCaptions ? getLightboxCaption(source) : undefined,
                    description: undefined,
                }))}
                open={index >= 0 && index < photos.length}
                index={index}
                close={() => setIndex(-1)}
                plugins={[...(showLightboxCaptions ? [Captions] : []), Fullscreen, Slideshow, Zoom]}
                captions={{
                    showToggle: false,
                    descriptionTextAlign: 'center',
                }}
                carousel={{ finite: photos.length <= 1 }}
                controller={{ aria: true }}
                className="images-gallery-lightbox"
                labels={{ Caption: 'Image caption' }}
            />
        </section>
    );
};

export default ImagesGallery;
